import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS } from '@/lib/index';
import { services, testimonials, stats } from '@/data/index';
import { ServiceCard, TestimonialCard, StatCard } from '@/components/Cards';
import { CreditScoreComparison, BeforeAfterBar, CreditProgressChart } from '@/components/CreditVisuals';
import { Button } from '@/components/ui/button';
import { springPresets, fadeInUp, staggerContainer, staggerItem } from '@/lib/motion';

export default function Home() {
  return (
    <div className="min-h-screen relative">
      {/* Background gradient orbs */}
      <div className="gradient-orb-primary w-[600px] h-[600px] top-0 right-0" />
      <div className="gradient-orb-accent w-[500px] h-[500px] top-1/3 left-0" />
      <div className="gradient-orb-primary w-[400px] h-[400px] bottom-1/4 right-1/4" />

      <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.HERO_BG_9}
            alt="Professional financial consulting office"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        </div>

        <motion.div
          className="relative z-10 container mx-auto px-4 text-center"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springPresets.gentle}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent/10 border border-accent/20 mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ ...springPresets.snappy, delay: 0.2 }}
          >
            <CheckCircle className="w-4 h-4 text-accent" />
            <span className="text-sm font-medium text-accent">AI-Powered Financial Services</span>
          </motion.div>

          <motion.h1
            className="text-5xl md:text-7xl font-bold mb-6 tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.3 }}
          >
            From Credit Repair to
            <br />
            <span className="gradient-text">Millions in Wealth</span>
          </motion.h1>

          <motion.p
            className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.4 }}
          >
            Transform your credit score, save thousands in taxes, and access business funding. We guide you from credit repair to building real wealth - step by step, with AI-powered tools and expert support.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springPresets.gentle, delay: 0.5 }}
          >
            <Button asChild size="lg" className="text-lg px-8 py-6 glow-primary hover:glow-primary transition-all">
              <Link to={ROUTE_PATHS.CONTACT}>
                Get Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6 border-primary/30 hover:bg-primary/10">
              <Link to={ROUTE_PATHS.SERVICES}>Explore Services</Link>
            </Button>
          </motion.div>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springPresets.gentle, delay: 0.8, repeat: Infinity, repeatType: 'reverse', duration: 1.5 }}
        >
          <a href="#services" className="text-muted-foreground hover:text-foreground transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </a>
        </motion.div>
      </section>

      <section id="services" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Everything You Need to Win Financially</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Not just services - complete roadmaps to buying a house, getting funded, and building real wealth
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service) => (
              <motion.div key={service.id} variants={staggerItem}>
                <ServiceCard service={service} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="stats" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Proven Results</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Our track record speaks for itself—real numbers from real clients
            </p>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat) => (
              <motion.div key={stat.id} variants={staggerItem}>
                <StatCard stat={stat} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="results" className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">See The Transformation</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Real credit score improvements from real clients - not just promises, actual results
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto space-y-12">
            {/* Before/After Comparison */}
            <CreditScoreComparison
              beforeScores={{ equifax: 580, experian: 592, transunion: 575 }}
              afterScores={{ equifax: 720, experian: 715, transunion: 728 }}
              timeframe="8 months"
            />

            {/* Individual Bureau Progress Bars */}
            <div className="grid gap-6">
              <BeforeAfterBar before={580} after={720} label="Equifax Score" />
              <BeforeAfterBar before={592} after={715} label="Experian Score" />
              <BeforeAfterBar before={575} after={728} label="TransUnion Score" />
            </div>

            {/* Credit Journey Chart */}
            <CreditProgressChart
              title="Typical 12-Month Credit Journey"
              data={[
                { month: 'Jan', score: 585 },
                { month: 'Mar', score: 612 },
                { month: 'May', score: 645 },
                { month: 'Jul', score: 678 },
                { month: 'Sep', score: 695 },
                { month: 'Nov', score: 715 },
                { month: 'Dec', score: 721 },
              ]}
            />
          </div>
        </div>
      </section>

      <section id="testimonials" className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={springPresets.gentle}
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Client Success Stories</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Hear from clients who transformed their financial lives with our services
            </p>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {testimonials.slice(0, 6).map((testimonial) => (
              <motion.div key={testimonial.id} variants={staggerItem}>
                <TestimonialCard testimonial={testimonial} />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="cta" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.CONSULTING_1}
            alt="Business consulting meeting"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
        </div>

        <motion.div
          className="relative z-10 container mx-auto px-4 text-center"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={springPresets.gentle}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Financial Life?
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-12">
            Get your free consultation and personalized roadmap. Whether you want to buy a house, get business funding, or just fix your credit - we'll show you exactly how to get there.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button asChild size="lg" className="text-lg px-8 py-6 shadow-lg hover:shadow-xl transition-all">
              <Link to={ROUTE_PATHS.CONTACT}>
                Book Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-lg px-8 py-6">
              <Link to={ROUTE_PATHS.ABOUT}>Learn About Us</Link>
            </Button>
          </div>

          <motion.div
            className="mt-16 grid sm:grid-cols-3 gap-8 max-w-4xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={staggerItem} className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">No Obligation</h3>
              <p className="text-sm text-muted-foreground">Free consultation with no commitment required</p>
            </motion.div>

            <motion.div variants={staggerItem} className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Expert Guidance</h3>
              <p className="text-sm text-muted-foreground">Personalized advice from certified professionals</p>
            </motion.div>

            <motion.div variants={staggerItem} className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Fast Results</h3>
              <p className="text-sm text-muted-foreground">AI-powered solutions deliver results 3x faster</p>
            </motion.div>
          </motion.div>
        </motion.div>
      </section>
    </div>
  );
}